import {
  allowOperationsConverter,
  citiesFiltersConverter,
  exportCsvConverter,
  locationHostFilterDataConverter,
  pagableDataConverter,
} from 'data/convertors/general.converters';
import {
  attendancesEditConverter,
  eventAttendancesEditConverter,
  getRegistrantConvertor,
  getWaitListConvertor,
  waitListFilterConverter,
  waitListMoveConverter,
  registrantsFilterConverter,
  registrantRemoveConverter,
  registrantRegisterConverter,
  childcareFilterConverter,
  getRegistrantsWithChildcareConvertor,
  editRegistrantsChildcareConvertor,
  getRegistrantsNoChildcareConvertor,
  waitListMoveEventConverter,
  editEventRegistrantsChildcareConvertor, // New converter for event registrants childcare
  eventRegistrantsNoChildcareConvertor, // New converter for event registrants no childcare
  eventRegistrantsWithChildcareConvertor,
  getEventRegistrantConvertor, // New converter for event registrants with childcare
  eventRegistrantRemoveConverter, // New converter for event registrant remove
  eventRegistrantRegisterConverter, // New converter for event registrant register
  getEventWaitListConvertor, // New converter for event wait list
} from 'data/convertors/registrants.convertor';
import {
  TAllowedOperationsDB,
  TAllowedOperationsUI,
  TExportCsvDB,
  TExportCsvUI,
  TExportFilters,
  TLocationHostFilterDataDB,
  TPageableDataWithContentDB,
  TPageableDataWithContentUI,
  TTableFilters,
} from 'data/types/generic.types';
import { TColumnFilterItem } from 'components/Base/Table/Table.types';
import {
  EWaitListColumnKey,
  TAttendancesEditUI,
  TEventAttendancesEditUI,
  TRegistrantDB,
  TRegistrantUI,
  TRemoveRegistrantUI,
  TRegisterRegistrantUI,
  TWaitListDB,
  TWaitListMoveUI,
  TWaitListUI,
  TRegistrantsWithChildcareUI,
  EChildcareColumnKey,
  TRegistrantsWithChildcareDB,
  TEditRegitrantChildcareUI,
  TRegistrantsNoChildcareDB,
  TRegistrantsNoChildcareUI,
  ERegistrantsColumnKey,
  TUpdateRegistrant,
  TWaitListMoveEventUI,
  TEventRegistrantNoChildcareUI, // New type for Event Registrant No Childcare
  TEventEditRegistrantChildcareUI, // New type for Event Edit Registrant Childcare
  TEventRegistrantsWithChildcareUI, // New type for Event Registrants With Childcare
  TEventRegistrantsWithChildcareDB,
  TEventRegistrantsNoChildcareDB,
  TEventRegistrantUI,
  TEventRegistrantDB,
  TEventRemoveRegistrantUI,
  TEventRegisterRegistrantUI, // New type for Event Registrants With Childcare DB
  TEventWaitListUI, // New type for Event Wait List UI
  TEventWaitListDB, // New type for Event Wait List DB
  TUpdateRegistrantMaterialsLanguageUI,
} from 'data/types/registrants.types';
import {
  ERegistrantsUrls,
  getWaitlistLocations,
  reRegisterRegistrantsUrl,
  registrantsChildcareUrl,
  registrantsNoChildcareUrl,
  removeRegistrantsUrl,
  unRegisterRegistrantsUrl,
  updateAttendanceUrl,
  updateMaterialsLanguageUrl,
} from 'data/urls/registrants.url';
import { authSplitApi } from 'redux/helpers/slice.helpers';
import { downloadFile } from 'redux/services/fileServices';
import { workshopsApi } from './workshops.slice';
import { kitManagementApi } from './kitManagement.slice';
import { eventsApi } from './events.slice';
import { organizationKitInventoryApi } from './organizationKitInventory.slice';

const baseUrl = `${ERegistrantsUrls.BASE_URL}`;

export const registrantsApi = authSplitApi('registrants', [
  'workshop-registrants',
  'event-registrants',
  'wait-list',
  'wait-list-cities',
  'wait-list-locations',
  'registrants-with-childcare',
  'registrants-no-childcare',
  'event-registrants-no-childcare', // New tag for event registrants no childcare
  'event-registrants-childcare', // New tag for event registrants childcare
]).injectEndpoints({
  endpoints: build => ({
    getRegistrants: build.query<
      TPageableDataWithContentUI<TRegistrantUI[]> & {
        allowedOperations: TAllowedOperationsUI;
      },
      TTableFilters & { workshopId?: string }
    >({
      query({
        workshopId,
        page,
        pageSize,
        filters,
        sortColumn: { sortDirection, sortField },
      }) {
        return {
          url: `${baseUrl}/${ERegistrantsUrls.REGISTRANTS}`,
          method: 'GET',
          params: {
            page,
            size: pageSize,
            ...(workshopId && { workshopId }),
            ...(sortField && sortDirection && { sortField, sortDirection }),
            ...(filters && {
              ...registrantsFilterConverter(filters),
            }),
          },
        };
      },
      providesTags: (result, error, { workshopId }) => {
        if (workshopId) {
          return [{ type: 'workshop-registrants', id: workshopId }];
        }
        return [];
      },
      transformResponse: (
        data: TPageableDataWithContentDB<TRegistrantDB[]> & {
          allowedOperations: TAllowedOperationsDB;
        },
      ) => {
        return {
          ...pagableDataConverter.fromDb(data),
          content: (data.content || []).map(workshop =>
            getRegistrantConvertor.fromDb(workshop),
          ),
          allowedOperations: allowOperationsConverter.fromDb(
            data.allowedOperations,
          ),
        };
      },
    }),
    getEventRegistrants: build.query<
      TPageableDataWithContentUI<TEventRegistrantUI[]> & {
        allowedOperations: TAllowedOperationsUI;
      },
      TTableFilters & { eventId?: string }
    >({
      query({
        eventId,
        page,
        pageSize,
        filters,
        sortColumn: { sortDirection, sortField },
      }) {
        return {
          url: `events/${ERegistrantsUrls.REGISTRANTS}`,
          method: 'GET',
          params: {
            page,
            size: pageSize,
            ...(eventId && { eventId }),
            ...(sortField && sortDirection && { sortField, sortDirection }),
            ...(filters && {
              ...registrantsFilterConverter(filters),
            }),
          },
        };
      },
      providesTags: (result, error, { eventId }) => {
        if (eventId) {
          return [{ type: 'event-registrants', id: eventId }];
        }
        return [];
      },
      transformResponse: (
        data: TPageableDataWithContentDB<TEventRegistrantDB[]> & {
          allowedOperations: TAllowedOperationsDB;
        },
      ) => {
        return {
          ...pagableDataConverter.fromDb(data),
          content: (data.content || []).map(registrant =>
            getEventRegistrantConvertor.fromDb(registrant),
          ),
          allowedOperations: allowOperationsConverter.fromDb(
            data.allowedOperations,
          ),
        };
      },
    }),
    updateAttendance: build.mutation<void, TAttendancesEditUI>({
      query(body) {
        return {
          url: updateAttendanceUrl,
          method: 'PATCH',
          body: attendancesEditConverter.toDb(body),
        };
      },
      invalidatesTags: (result, error, { workshopId }) => [
        { type: 'workshop-registrants', id: workshopId },
      ],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            // GROW-4315: the session card Attendance badge reads attendeesCount
            // from getWorkshopById (workshopsApi, tag 'workshop'), which the BE
            // recomputes on every attendance update (GROW-4303). Invalidate it
            // here so the badge refreshes without a page reload.
            dispatch(workshopsApi.util.invalidateTags(['workshop']));
            dispatch(
              kitManagementApi.util.invalidateTags(['kit-management-parents']),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    // GROW-4365: change a registrant's kit (materials) language. The row is
    // patched optimistically in every cached getRegistrants entry of the
    // workshop (Registrants + Attendance tabs share that query) and rolled back
    // if the request fails. On success the BE has moved the kit deduction to
    // the new language, so Kit Management inventory + history are refreshed.
    updateRegistrantMaterialsLanguage: build.mutation<
      void,
      TUpdateRegistrantMaterialsLanguageUI
    >({
      query({ workshopId, registrantId, materialsLanguage }) {
        return {
          url: updateMaterialsLanguageUrl,
          method: 'PATCH',
          body: { workshopId, registrantId, materialsLanguage },
        };
      },
      invalidatesTags: (result, error, { workshopId }) => [
        { type: 'workshop-registrants', id: workshopId },
      ],
      async onQueryStarted(
        { workshopId, registrantId, materialsLanguage },
        { dispatch, getState, queryFulfilled },
      ) {
        const patches = registrantsApi.util
          .selectInvalidatedBy(getState(), [
            { type: 'workshop-registrants', id: workshopId },
          ])
          .filter(entry => entry.endpointName === 'getRegistrants')
          .map(({ originalArgs }) =>
            dispatch(
              registrantsApi.util.updateQueryData(
                'getRegistrants',
                originalArgs as TTableFilters & { workshopId?: string },
                draft => {
                  const registrant = draft.content.find(
                    item => item.registrantId === registrantId,
                  );
                  if (registrant) {
                    registrant.materialsLanguage = materialsLanguage;
                  }
                },
              ),
            ),
          );
        try {
          await queryFulfilled;
          dispatch(
            organizationKitInventoryApi.util.invalidateTags([
              'organization-kit-inventory',
              'organization-kit-inventory-history',
            ]),
          );
          dispatch(
            kitManagementApi.util.invalidateTags(['kit-management-parents']),
          );
        } catch {
          patches.forEach(patch => patch.undo());
        }
      },
    }),
    updateEventAttendance: build.mutation<void, TEventAttendancesEditUI>({
      query(body) {
        return {
          url: `events/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.ATTENDANCE}`,
          method: 'PATCH',
          body: eventAttendancesEditConverter.toDb(body),
        };
      },
      invalidatesTags: (result, error, { eventId }) => [
        { type: 'event-registrants', id: eventId },
      ],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            // GROW-4340: the Details tab Attended count reads attendees from
            // getEventById (eventsApi, tag 'event'), which the BE recalculates
            // on every attendance update (GROW-4289). Invalidate it (and the
            // list Participation column) so it refreshes without a reload.
            dispatch(eventsApi.util.invalidateTags(['events', 'event']));
            dispatch(
              kitManagementApi.util.invalidateTags(['kit-management-parents']),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    getWaitList: build.query<
      TPageableDataWithContentUI<TWaitListUI[]> & {
        allowedOperations: TAllowedOperationsUI;
      },
      TTableFilters<EWaitListColumnKey> & {
        workshopId?: string;
        activeOnly?: boolean;
      }
    >({
      query({
        page,
        pageSize,
        sortColumn: { sortDirection, sortField },
        filters,
        workshopId,
        activeOnly,
      }) {
        return {
          url: `${baseUrl}/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.WAIT_LIST}`,
          method: 'GET',
          params: {
            page,
            size: pageSize,
            ...(sortField && sortDirection && { sortField, sortDirection }),
            ...(workshopId && { workshopId }),
            ...(activeOnly !== undefined && { activeOnly }),
            ...(filters && {
              ...waitListFilterConverter(filters),
            }),
          },
        };
      },
      providesTags: ['wait-list'],
      transformResponse: (
        data: TPageableDataWithContentDB<TWaitListDB[]> & {
          allowedOperations: TAllowedOperationsDB;
        },
      ) => {
        return {
          ...pagableDataConverter.fromDb(data),
          content: (data.content || []).map(waitList =>
            getWaitListConvertor.fromDb(waitList),
          ),
          allowedOperations: allowOperationsConverter.fromDb(
            data.allowedOperations,
          ),
        };
      },
    }),
    getWaitListCities: build.query<TColumnFilterItem[], void>({
      query() {
        return {
          url: `${baseUrl}/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.WAIT_LIST}/${ERegistrantsUrls.CITIES}`,
          method: 'GET',
        };
      },
      providesTags: ['wait-list-cities'],
      transformResponse: (data: string[]) =>
        citiesFiltersConverter.fromDb(data),
      keepUnusedDataFor: 3600,
    }),
    getValidateExport: build.query<boolean, { workshopId: string }>({
      query({ workshopId }) {
        return {
          url: `${baseUrl}/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.EXPORT}/${ERegistrantsUrls.VALIDATE}`,
          method: 'GET',
          params: { workshopId },
        };
      },
    }),
    exportRegistrants: build.mutation<
      TExportCsvUI,
      {
        columns: string[];
        filters: Record<string, unknown>;
        sort?: { field: string; direction: string };
      }
    >({
      query: body => ({
        url: `${baseUrl}/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.EXPORT}`,
        method: 'POST',
        body,
      }),
      transformResponse: (data: TExportCsvDB) => {
        const convertedData = exportCsvConverter('csv').fromDb(data);
        downloadFile(convertedData.content, convertedData.fileName);
        return convertedData;
      },
    }),
    exportWaitlist: build.mutation<
      TExportCsvUI,
      {
        columns: string[];
        filters?: Record<string, unknown>;
        sort?: { field: string; direction: string };
      }
    >({
      query: body => ({
        url: `${baseUrl}/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.WAIT_LIST}/${ERegistrantsUrls.EXPORT}`,
        method: 'POST',
        body,
      }),
      transformResponse: (data: TExportCsvDB) => {
        const convertedData = exportCsvConverter('csv').fromDb(data);
        downloadFile(convertedData.content, convertedData.fileName);
        return convertedData;
      },
    }),
    exportAttendance: build.mutation<
      TExportCsvUI,
      {
        columns: string[];
        filters: Record<string, unknown>;
        sort?: { field: string; direction: string };
      }
    >({
      query: body => ({
        url: `${baseUrl}/${ERegistrantsUrls.ATTENDANCE}/${ERegistrantsUrls.EXPORT}`,
        method: 'POST',
        body,
      }),
      transformResponse: (data: TExportCsvDB) => {
        const convertedData = exportCsvConverter('csv').fromDb(data);
        downloadFile(convertedData.content, convertedData.fileName);
        return convertedData;
      },
    }),
    getWaitListLocations: build.query<TColumnFilterItem[], void>({
      query() {
        return {
          url: getWaitlistLocations,
          method: 'GET',
        };
      },
      transformResponse: (data: TLocationHostFilterDataDB[]) => {
        return locationHostFilterDataConverter.fromDb(data);
      },
      providesTags: ['wait-list-locations'],
      keepUnusedDataFor: 3600,
    }),
    moveAction: build.mutation<void, TWaitListMoveUI>({
      query(body) {
        return {
          url: `${baseUrl}/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.WAIT_LIST}/${ERegistrantsUrls.MOVE}`,
          method: 'PATCH',
          body: waitListMoveConverter.toDb(body),
        };
      },
      invalidatesTags: (result, error, { workshopId }) => [
        { type: 'workshop-registrants', id: workshopId },
        'wait-list-cities',
        'wait-list-locations',
        'wait-list',
      ],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              workshopsApi.endpoints.getWorkshopById.initiate({
                id: arg.workshopId || '',
              }),
            );
            dispatch(
              workshopsApi.util.invalidateTags(['workshops', 'workshop']),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    deleteWaitListRegistrant: build.mutation<
      void,
      { workshopId: string; registrantId: string }
    >({
      query(body) {
        return {
          url: `${baseUrl}/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.WAIT_LIST}/delete`,
          method: 'DELETE',
          body,
        };
      },
      invalidatesTags: (result, error, { workshopId }) => [
        { type: 'workshop-registrants', id: workshopId },
        'wait-list',
        'wait-list-cities',
        'wait-list-locations',
      ],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              workshopsApi.util.invalidateTags(['workshops', 'workshop']),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    deleteEventWaitListRegistrant: build.mutation<
      void,
      { eventId: string; registrantId: string }
    >({
      query(body) {
        return {
          url: `events/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.WAIT_LIST}/delete`,
          method: 'DELETE',
          body,
        };
      },
      invalidatesTags: (result, error, { eventId }) => [
        { type: 'event-registrants', id: eventId },
        { type: 'wait-list', id: eventId },
        'wait-list',
        'wait-list-cities',
        'wait-list-locations',
      ],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(eventsApi.util.invalidateTags(['events', 'event']));
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    moveEventAction: build.mutation<void, TWaitListMoveEventUI>({
      query(body) {
        return {
          url: `events/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.WAIT_LIST}/${ERegistrantsUrls.MOVE}`,
          method: 'PATCH',
          body: waitListMoveEventConverter.toDb(body),
        };
      },
      invalidatesTags: (result, error, { eventId }) => [
        { type: 'event-registrants', id: eventId },
        'wait-list-cities',
        'wait-list-locations',
        'wait-list',
      ],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              eventsApi.endpoints.getEventById.initiate({
                id: arg.eventId || '',
              }),
            );
            dispatch(eventsApi.util.invalidateTags(['events', 'event']));
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    removeRegistrant: build.mutation<void, TRemoveRegistrantUI>({
      query(body) {
        return {
          url: removeRegistrantsUrl,
          method: 'DELETE',
          body: registrantRemoveConverter.toDb(body),
        };
      },
      invalidatesTags: (result, error, { workshopId }) => [
        { type: 'workshop-registrants', id: workshopId },
      ],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              workshopsApi.util.invalidateTags(['workshops', 'workshop']),
            );
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-parents',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    unRegisterRegistrant: build.mutation<void, TRegisterRegistrantUI>({
      query(body) {
        return {
          url: unRegisterRegistrantsUrl,
          method: 'PATCH',
          body: registrantRegisterConverter.toDb(body),
        };
      },
      invalidatesTags: (result, error, { workshopId }) => [
        { type: 'workshop-registrants', id: workshopId },
      ],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              workshopsApi.util.invalidateTags(['workshops', 'workshop']),
            );
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
                'kit-management-parents',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    reRegisterRegistrant: build.mutation<void, TRegisterRegistrantUI>({
      query(body) {
        return {
          url: reRegisterRegistrantsUrl,
          method: 'PATCH',
          body: registrantRegisterConverter.toDb(body),
        };
      },
      invalidatesTags: (result, error, { workshopId }) => [
        { type: 'workshop-registrants', id: workshopId },
      ],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              workshopsApi.util.invalidateTags(['workshops', 'workshop']),
            );
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
                'kit-management-parents',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    getRegistrantsWithChildcare: build.query<
      TPageableDataWithContentUI<TRegistrantsWithChildcareUI[]> & {
        allowedOperations: TAllowedOperationsUI;
      },
      TTableFilters<EChildcareColumnKey> & {
        workshopId?: string;
      }
    >({
      query({
        page,
        pageSize,
        sortColumn: { sortDirection, sortField },
        filters,
        workshopId,
      }) {
        return {
          url: registrantsChildcareUrl,
          method: 'GET',
          params: {
            page,
            size: pageSize,
            ...(sortField && sortDirection && { sortField, sortDirection }),
            ...(workshopId && { workshopId }),
            ...(filters && {
              ...childcareFilterConverter(filters),
            }),
          },
        };
      },
      providesTags: ['registrants-with-childcare'],
      transformResponse: (
        data: TPageableDataWithContentDB<TRegistrantsWithChildcareDB[]> & {
          allowedOperations: TAllowedOperationsDB;
        },
      ) => {
        return {
          ...pagableDataConverter.fromDb(data),
          content: (data.content || []).map(childcare =>
            getRegistrantsWithChildcareConvertor.fromDb(childcare),
          ),
          allowedOperations: allowOperationsConverter.fromDb(
            data.allowedOperations,
          ),
        };
      },
    }),
    editChildcare: build.mutation<void, TEditRegitrantChildcareUI>({
      query(body) {
        return {
          url: registrantsChildcareUrl,
          method: 'PATCH',
          body: editRegistrantsChildcareConvertor.toDb(body),
        };
      },
      invalidatesTags: [
        'registrants-with-childcare',
        'registrants-no-childcare',
      ],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              workshopsApi.util.invalidateTags(['workshops-childCare-count']),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    getRegistrantsNoChildcare: build.query<
      TRegistrantsNoChildcareUI[],
      {
        workshopId?: string;
      }
    >({
      query({ workshopId }) {
        return {
          url: registrantsNoChildcareUrl,
          method: 'GET',
          params: {
            ...(workshopId && { workshopId }),
          },
        };
      },
      providesTags: ['registrants-no-childcare'],
      transformResponse: (data: TRegistrantsNoChildcareDB[]) =>
        (data || []).map(childcare =>
          getRegistrantsNoChildcareConvertor.fromDb(childcare),
        ),
    }),
    updateRegistrant: build.mutation<
      void,
      { workshopId: string; registrantId: string; workshopClassId: string }
    >({
      query(body: TUpdateRegistrant) {
        return {
          url: `${baseUrl}/${ERegistrantsUrls.REGISTRANTS}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: (result, error, { workshopId }) => [
        { type: 'workshop-registrants', id: workshopId },
      ],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              workshopsApi.util.invalidateTags(['workshops', 'workshop']),
            );
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-parents',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    deleteRegistrant: build.mutation<
      void,
      { workshopId: string; registrantId: string }
    >({
      query(body) {
        return {
          url: removeRegistrantsUrl,
          method: 'DELETE',
          body,
        };
      },
      invalidatesTags: (result, error, { workshopId }) => [
        { type: 'workshop-registrants', id: workshopId },
      ],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(
              workshopsApi.util.invalidateTags(['workshops', 'workshop']),
            );
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-parents',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    getEventRegistrantsNoChildcare: build.query<
      TEventRegistrantNoChildcareUI[],
      { eventId: string }
    >({
      query({ eventId }) {
        return {
          url: `/events/registrants/no-childcare`,
          method: 'GET',
          params: {
            ...(eventId && { eventId }),
          },
        };
      },
      providesTags: ['event-registrants-no-childcare'],
      transformResponse: (data: TEventRegistrantsNoChildcareDB[]) =>
        data?.map(item => eventRegistrantsNoChildcareConvertor.fromDb(item)) ||
        [],
    }),
    editEventRegistrantChildcare: build.mutation<
      void,
      TEventEditRegistrantChildcareUI
    >({
      query(body) {
        return {
          url: `/events/registrants/childcare`,
          method: 'PATCH',
          body: editEventRegistrantsChildcareConvertor.toDb(body),
        };
      },
      invalidatesTags: [
        'event-registrants',
        'event-registrants-childcare',
        'event-registrants-no-childcare',
      ],
    }),
    getEventRegistrantsWithChildcare: build.query<
      TPageableDataWithContentUI<TEventRegistrantsWithChildcareUI[]> & {
        allowedOperations: TAllowedOperationsUI;
      },
      TTableFilters<EChildcareColumnKey> & {
        eventId?: string;
      }
    >({
      query({
        eventId,
        page,
        pageSize,
        sortColumn: { sortDirection, sortField },
        filters,
      }) {
        return {
          url: `/events/registrants/childcare`,
          method: 'GET',
          params: {
            page,
            size: pageSize,
            ...(sortField && sortDirection && { sortField, sortDirection }),
            ...(eventId && { eventId }),
            ...(filters && {
              ...childcareFilterConverter(filters),
            }),
          },
        };
      },
      providesTags: (result, error, { eventId }) => {
        if (eventId) {
          return [{ type: 'event-registrants-childcare', id: eventId }];
        }
        return ['event-registrants-childcare'];
      },
      transformResponse: (
        data: TPageableDataWithContentDB<TEventRegistrantsWithChildcareDB[]> & {
          allowedOperations: TAllowedOperationsDB;
        },
      ) => {
        return {
          ...pagableDataConverter.fromDb(data),
          content: (data.content || []).map(item =>
            eventRegistrantsWithChildcareConvertor.fromDb(item),
          ),
          allowedOperations: allowOperationsConverter.fromDb(
            data.allowedOperations,
          ),
        };
      },
    }),
    removeEventRegistrant: build.mutation<void, TEventRemoveRegistrantUI>({
      query(body) {
        return {
          url: `events/${ERegistrantsUrls.REGISTRANTS}`,
          method: 'DELETE',
          body: eventRegistrantRemoveConverter.toDb(body),
        };
      },
      invalidatesTags: (result, error, { eventId }) => [
        { type: 'event-registrants', id: eventId },
        'event-registrants-childcare',
        'event-registrants-no-childcare',
      ],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(eventsApi.util.invalidateTags(['events', 'event']));
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-parents',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    unRegisterEventRegistrant: build.mutation<void, TEventRegisterRegistrantUI>(
      {
        query(body) {
          return {
            url: `events/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.UNREGISTER}`,
            method: 'PATCH',
            body: eventRegistrantRegisterConverter.toDb(body),
          };
        },
        invalidatesTags: (result, error, { eventId }) => [
          { type: 'event-registrants', id: eventId },
          'event-registrants-childcare',
          'event-registrants-no-childcare',
        ],
        onQueryStarted(arg, { dispatch, queryFulfilled }) {
          queryFulfilled
            .then(() => {
              dispatch(eventsApi.util.invalidateTags(['events', 'event']));
              dispatch(
                kitManagementApi.util.invalidateTags([
                  'kit-management-parent-locations',
                  'kit-management-parent-hosts',
                  'kit-management-parents',
                ]),
              );
            })
            .catch(() => {
              // do nothing
            });
        },
      },
    ),
    reRegisterEventRegistrant: build.mutation<void, TEventRegisterRegistrantUI>(
      {
        query(body) {
          return {
            url: `events/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.REREGISTER}`,
            method: 'PATCH',
            body: eventRegistrantRegisterConverter.toDb(body),
          };
        },
        invalidatesTags: (result, error, { eventId }) => [
          { type: 'event-registrants', id: eventId },
          'event-registrants-childcare',
          'event-registrants-no-childcare',
        ],
        onQueryStarted(arg, { dispatch, queryFulfilled }) {
          queryFulfilled
            .then(() => {
              dispatch(eventsApi.util.invalidateTags(['events', 'event']));
              dispatch(
                kitManagementApi.util.invalidateTags([
                  'kit-management-parent-locations',
                  'kit-management-parent-hosts',
                  'kit-management-parents',
                ]),
              );
            })
            .catch(() => {
              // do nothing
            });
        },
      },
    ),
    updateEventRegistrant: build.mutation<
      void,
      { eventId: string; registrantId: string; eventClassId: string }
    >({
      query(body) {
        return {
          url: `events/${ERegistrantsUrls.REGISTRANTS}`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags: (result, error, { eventId }) => [
        { type: 'event-registrants', id: eventId },
        'event-registrants-childcare',
        'event-registrants-no-childcare',
      ],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(eventsApi.util.invalidateTags(['events', 'event']));
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-parents',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    deleteEventRegistrant: build.mutation<
      void,
      { eventId: string; registrantId: string }
    >({
      query(body) {
        return {
          url: `events/${ERegistrantsUrls.REGISTRANTS}`,
          method: 'DELETE',
          body,
        };
      },
      invalidatesTags: (result, error, { eventId }) => [
        { type: 'event-registrants', id: eventId },
        'event-registrants-childcare',
        'event-registrants-no-childcare',
      ],
      onQueryStarted(arg, { dispatch, queryFulfilled }) {
        queryFulfilled
          .then(() => {
            dispatch(eventsApi.util.invalidateTags(['events', 'event']));
            dispatch(
              kitManagementApi.util.invalidateTags([
                'kit-management-parents',
                'kit-management-parent-locations',
                'kit-management-parent-hosts',
              ]),
            );
          })
          .catch(() => {
            // do nothing
          });
      },
    }),
    getValidateEventExport: build.query<boolean, { eventId: string }>({
      query({ eventId }) {
        return {
          url: `events/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.EXPORT}/${ERegistrantsUrls.VALIDATE}`,
          method: 'GET',
          params: { eventId },
        };
      },
    }),
    exportEventRegistrantsCSV: build.query<
      TExportCsvUI,
      TExportFilters<ERegistrantsColumnKey> & { eventId: string }
    >({
      query({ filters, eventId }) {
        return {
          url: `events/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.EXPORT}`,
          method: 'GET',
          params: {
            ...(filters && {
              ...registrantsFilterConverter(filters),
            }),
            eventId,
          },
        };
      },
      transformResponse: (data: TExportCsvDB) => {
        const convertedData = exportCsvConverter('text/csv').fromDb(data);
        downloadFile(convertedData.content, convertedData.fileName);
        return convertedData;
      },
    }),
    getEventWaitList: build.query<
      TPageableDataWithContentUI<TEventWaitListUI[]> & {
        allowedOperations: TAllowedOperationsUI;
      },
      TTableFilters<EWaitListColumnKey> & {
        eventId?: string;
        activeOnly?: boolean;
      }
    >({
      query({
        page,
        pageSize,
        sortColumn: { sortDirection, sortField },
        filters,
        eventId,
        activeOnly,
      }) {
        return {
          url: `events/${ERegistrantsUrls.REGISTRANTS}/${ERegistrantsUrls.WAIT_LIST}`,
          method: 'GET',
          params: {
            page,
            size: pageSize,
            ...(sortField && sortDirection && { sortField, sortDirection }),
            ...(eventId && { eventId }),
            ...(activeOnly !== undefined && { activeOnly }),
            ...(filters && {
              ...waitListFilterConverter(filters),
            }),
          },
        };
      },
      providesTags: (result, error, { eventId }) => {
        if (eventId) {
          return [{ type: 'wait-list', id: eventId }];
        }
        return ['wait-list'];
      },
      transformResponse: (
        data: TPageableDataWithContentDB<TEventWaitListDB[]> & {
          allowedOperations: TAllowedOperationsDB;
        },
      ) => {
        return {
          ...pagableDataConverter.fromDb(data),
          content: (data.content || []).map(waitList =>
            getEventWaitListConvertor.fromDb(waitList),
          ),
          allowedOperations: allowOperationsConverter.fromDb(
            data.allowedOperations,
          ),
        };
      },
    }),
    updateWorkshopRegistrantNotification: build.mutation<
      void,
      {
        workshopId: string;
        registrantId: string;
        notificationDisabled: boolean;
      }
    >({
      query: ({ workshopId, registrantId, notificationDisabled }) => ({
        url: `${baseUrl}/${ERegistrantsUrls.REGISTRANTS}/notification-disabled`,
        method: 'PATCH',
        body: { workshopId, registrantId, notificationDisabled },
      }),
    }),
    updateEventRegistrantNotification: build.mutation<
      void,
      {
        eventId: string;
        registrantId: string;
        notificationDisabled: boolean;
      }
    >({
      query: ({ eventId, registrantId, notificationDisabled }) => ({
        url: `/events/${ERegistrantsUrls.REGISTRANTS}/notification-disabled`,
        method: 'PATCH',
        body: { eventId, registrantId, notificationDisabled },
      }),
    }),
  }),
});

export const {
  useGetRegistrantsQuery,
  useGetEventRegistrantsQuery,
  useUpdateAttendanceMutation,
  useUpdateRegistrantMaterialsLanguageMutation,
  useUpdateEventAttendanceMutation,
  useGetWaitListQuery,
  useGetWaitListCitiesQuery,
  useMoveActionMutation,
  useMoveEventActionMutation,
  useRemoveRegistrantMutation,
  useRemoveEventRegistrantMutation,
  useUnRegisterRegistrantMutation,
  useUnRegisterEventRegistrantMutation,
  useReRegisterRegistrantMutation,
  useReRegisterEventRegistrantMutation,
  useGetWaitListLocationsQuery,
  useGetRegistrantsWithChildcareQuery,
  useGetEventRegistrantsWithChildcareQuery,
  useGetRegistrantsNoChildcareQuery,
  useGetEventRegistrantsNoChildcareQuery,
  useEditChildcareMutation,
  useEditEventRegistrantChildcareMutation,
  useGetValidateExportQuery,
  useGetValidateEventExportQuery,
  useExportRegistrantsMutation,
  useLazyExportEventRegistrantsCSVQuery,
  useExportWaitlistMutation,
  useExportAttendanceMutation,
  useUpdateRegistrantMutation,
  useUpdateEventRegistrantMutation,
  useDeleteRegistrantMutation,
  useDeleteEventRegistrantMutation,
  useDeleteWaitListRegistrantMutation,
  useDeleteEventWaitListRegistrantMutation,
  useGetEventWaitListQuery,
  useUpdateWorkshopRegistrantNotificationMutation,
  useUpdateEventRegistrantNotificationMutation,
} = registrantsApi;
